// Response shapes for the operator read endpoints (ATC-051, ATC-052). These
// mirror the `data` payloads returned inside the standard ATC response
// envelope { apiVersion, requestId, status, timestamp, data, error }.

export interface EnvironmentListItem {
  customerEnvironmentId: string
  customerName: string
  /**
   * Hotfix 055. `entitledServices` below is the DEVICE credential's set.
   * `ppkg` is grantable on flightdeck-class rows ONLY (`ATC-ADR-016`), so it
   * can never appear there — and an operator looking at the list had no way to
   * tell whether an environment held a Flight Deck credential at all.
   *
   * OPTIONAL: rows written before the field existed report nothing, and a
   * console deployed ahead of the backend sees `undefined`. Absent renders as
   * "no record of one", never as a claim that one exists.
   */
  hasFlightdeckCredential?: boolean
  // ATC-127: was an inline literal union while `EnvironmentDetail` used the
  // named `EnvironmentStatus`. Identical members, so this is a naming fix, not
  // a narrowing — but two spellings of one closed enum is how the two drift.
  status: EnvironmentStatus
  accountId: string | null
  entitledServices: string[]
  issuedAt: string
  expiresAt: string | null
}

export interface EnvironmentListResponse {
  environments: EnvironmentListItem[]
  continuationToken: string | null
}

export interface ConsumableBalance {
  granted: number
  burned: number
  reservedOpen: number
  available: number
  // Hotfix 052. INFORMATIONAL, always <= 0, and NEVER used in arithmetic: the
  // signed sum of adjustment rows for this bucket. It answers "why does this
  // say 40 when I granted 50", which the net figures alone cannot. It is
  // already inside `available` — subtracting it would double-count.
  adjustments: number
}

export interface PoolBalance {
  capacity: number
  inUse: number
  available: number
  adjustments: number
}

export interface MigrationBalance {
  consumable: ConsumableBalance
  pool: PoolBalance
  reclaimedOpen: number
}

export interface EnvironmentDetail {
  customerEnvironmentId: string
  customerName: string
  // ATC-109: narrowed from `string` so the status-tone switch in
  // `theme/statusTone.ts` can carry a compile-time exhaustiveness guard — the
  // guard cannot exist against `string`. Not a wire change: the server has
  // always sent exactly these three values, and Licensing's PATCH lifecycle
  // enumerates them. The runtime `default` branch in `environmentStatusTone`
  // is what covers the server sending something outside the union anyway.
  status: EnvironmentStatus
  entitledServices: string[]
  issuedAt: string
  expiresAt: string | null
  account: { accountId: string } | null
  balance: MigrationBalance | null
  // ATC-123 added this server-side. OPTIONAL here deliberately: a console
  // deployed ahead of the backend degrades to today's single-credential view
  // rather than rendering `undefined.map`. Required would make the type a
  // claim about deploy order that the type cannot enforce.
  credentials?: EnvironmentCredential[]
}

export interface AccountEnvironment {
  customerEnvironmentId: string
  accountName: string
  addedAt: string
}

export interface AccountEnvironmentsResponse {
  accountId: string
  environments: AccountEnvironment[]
}

export interface Device {
  serialNumber: string
  migrationId: string
  migrationType: string
  apiBaseUrl: string
  bootstrapTemplateUrl: string
  registeredAt: string
  lastResolvedAt: string | null
}

export interface DeviceListResponse {
  customerEnvironmentId: string
  devices: Device[]
}

export interface EnvironmentListQuery {
  status?: string
  search?: string
  pageSize?: number
  continuationToken?: string
  /**
   * ATC-141. `ATC-120` added this filter to `List-Environments` server-side and
   * NO CLIENT CODE EVER SENT IT — the query type never carried it, so it was
   * unreachable from the console for the whole of Sprint 9. Benign compared to
   * Hotfix 054 (a filter nobody called, rather than a field silently dropped),
   * but the same shape: shipped, tested server-side, and unreachable.
   */
  accountId?: string
}

// --- Write-action responses (ATC-056) ---

// Returned by issuance and by token rotation — both surface the raw license
// token exactly once.
export interface TokenIssuanceResponse {
  customerEnvironmentId: string
  licenseToken: string
  customerName: string
  issuedAt: string
  // ATC-139. The server has always returned these three; the type declared
  // four fields, so the console could not read back what it had just asked
  // for. `expiresAt` in particular is what Hotfix 054 needed and nobody had.
  //
  // OPTIONAL, and the optionality is load-bearing: `undefined` means an older
  // deployment did not report the field, which `verifyExpiry` must distinguish
  // from `null` — the server explicitly saying "perpetual".
  principalClass?: PrincipalClass
  entitledServices?: string
  expiresAt?: string | null
}

/**
 * ATC-142. What a delete destroyed, reported back so the operator sees it once
 * — the rows are gone, and the App Insights `EnvironmentDeleted` event is the
 * only place this information continues to exist.
 */
export interface EnvironmentDeleteResponse {
  customerEnvironmentId: string
  /**
   * The ENVIRONMENT's label, not a customer name. The storage field is still
   * called `customerName` and the rename is deferred, so the wire name here
   * says what the value actually means (ATC-141).
   */
  environmentLabel: string
  accountId: string | null
  credentialsDeleted: number
  principalClasses: PrincipalClass[]
  deletedAt: string
}

export interface PatchStatusResponse {
  customerEnvironmentId: string
  status: string
}

export interface MapAccountResponse {
  accountId: string
  customerEnvironmentId: string
  accountName: string
  addedAt: string
}

export type MigrationLicenseType = 'consumable' | 'pool'

// --- Provisioning (R-ATC-10) ---

/**
 * `ATC-ADR-016`'s FD-distinct principal class. Absence on the wire reads as
 * `device`; `Get-EnvironmentDetail` normalises it so an empty string never
 * reaches this type.
 */
export type PrincipalClass = 'device' | 'flightdeck'

/**
 * The service scopes a credential may call. Mirrors
 * `Patch-CustomerEnvironment`'s `$supportedEntitlements`.
 *
 * A value the console does not recognise must still round-trip: an entitlement
 * absent from an edit's `before` set is one the delta would SILENTLY REMOVE.
 * That is why the union is widened at the point of use rather than treated as
 * exhaustive here.
 */
export type Entitlement = 'bootstrap' | 'mint' | 'licensing' | 'readiness' | 'ppkg'

export const SUPPORTED_ENTITLEMENTS: Entitlement[] = [
  'bootstrap',
  'mint',
  'licensing',
  'readiness',
  'ppkg',
]

/** One credential row on an environment (ATC-123). Never carries token material. */
export interface EnvironmentCredential {
  principalClass: PrincipalClass
  status: EnvironmentStatus
  entitledServices: string[]
  issuedAt: string
  expiresAt: string | null
}

export interface AccountSummary {
  accountId: string
  accountName: string
  status: 'active' | 'archived'
  createdAt: string
  // `null` when the membership scan failed — NOT 0. The server distinguishes
  // "no environments" from "could not tell", and collapsing them here would
  // undo that.
  environmentCount: number | null
}

export interface AccountListResponse {
  accounts: AccountSummary[]
  continuationToken: string | null
}

export interface AccountListQuery {
  status?: string
  search?: string
  pageSize?: number
  continuationToken?: string
}

// The registry's own record of one account (ATC-137). Deliberately carries no
// balance and no environment count: a balance is an unbounded ledger partition
// scan, and `listAccountEnvironments` already returns the environments
// themselves to the one page that renders both.
export interface AccountDetail {
  accountId: string
  accountName: string
  status: string
  createdAt: string
}

export interface RenameAccountResponse {
  accountId: string
  accountName: string
  // false when the rename matched the value already held. The server still
  // audits it, following EntitlementChange's no-op convention.
  changed: boolean
}

export interface CreateAccountResponse {
  accountId: string
  accountName: string
  status: string
  createdAt: string
}

export interface AdjustmentResponse {
  accountId: string
  adjustmentId: string
  licenseType: MigrationLicenseType
  amount: number
  reason: string
  adjustedAt: string
  balance: MigrationBalance
}

export interface IssueEnvironmentOptions {
  // ATC-140. OPTIONAL: absent means the server mints a label. A caller may
  // still supply one — curl, the staging fixtures and every legacy caller keep
  // working, and existing typed names round-trip. Only the default changed.
  customerName?: string
  entitledServices?: Entitlement[]
  expiresAt?: string
  principalClass?: PrincipalClass
  /** Only for the flightdeck credential, which joins an EXISTING environment. */
  customerEnvironmentId?: string
}

export interface EntitlementEditResponse {
  customerEnvironmentId: string
  principalClass: PrincipalClass
  entitledServices: string[]
  changed: boolean
}

export interface ExpiryChangeResponse {
  customerEnvironmentId: string
  expiresAt: string | null
  /** `'all'` when no selector was sent — the status precedent, not rotation's. */
  principalClass: PrincipalClass | 'all'
  changed: boolean
}

export interface GrantResponse {
  accountId: string
  grantId: string
  licenseType: MigrationLicenseType
  amount: number
  grantedAt: string
}

export type EnvironmentStatus = 'active' | 'suspended' | 'revoked'

// --- Activity feed (ATC-057) ---

/**
 * The activity feed's outcome vocabulary — ATC-109.
 *
 * This is `shared/LoggingHelper.ps1`'s
 * `[ValidateSet('success', 'denied', 'not_found', 'error')]`, which every ATC
 * service writes and `Get-ActivityFeed` surfaces. `tokenParity.test.ts` asserts
 * this union against that ValidateSet so the two cannot drift.
 *
 * Note the UNDERSCORE in `not_found`: a helper that assumes camelCase breaks
 * on it.
 *
 * NOT the migration settle outcome. `docs/openapi/atc-migration-licensing.yaml`
 * carries an unrelated `success | failure | abort` for settling a migration
 * license; it never appears in this feed.
 *
 * Narrowing this is not an API contract change — it matches the server's
 * existing ValidateSet, and `Get-ActivityFeed` appears in no OpenAPI spec.
 */
export type ActivityOutcome = 'success' | 'denied' | 'not_found' | 'error'

export interface ActivityEvent {
  timestamp: string
  serviceName: string
  functionName: string
  operation: string
  outcome: ActivityOutcome
  auditEvent: string | null
  customerEnvironmentId: string | null
  accountId: string | null
  errorCode: string | null
  migrationId: string | null
}

export interface ActivityResponse {
  events: ActivityEvent[]
  filter: {
    customerEnvironmentId: string | null
    hours: number
    limit: number
  }
}

export interface ActivityQuery {
  customerEnvironmentId?: string
  hours?: number
  limit?: number
}

// --- Usage by migration (ATC-148 / `ATC-ADR-013` R-ATC-12) ---
//
// THE INVOICING SURFACE. A human bills a customer from this payload, which is
// why the server refuses every ambiguity rather than guessing: both bounds are
// required, `to` is exclusive so adjacent months partition the ledger exactly,
// and the window anchors on `reservedAt` — fixed server-side, never
// caller-selectable, so two operators cannot produce two statements for the
// same month.

/** One migration's consumption inside the requested window. */
export interface AccountUsageMigration {
  migrationId: string
  bucket: MigrationLicenseType
  /**
   * `device` or `campaign` — and deliberately NOT narrowed to a union.
   *
   * The console renders this value RAW rather than mapping it to a label or a
   * tone, so there is nothing here for an unrecognised value to fall through
   * to. Narrowing would be a claim about the server that the type cannot
   * enforce, and the raw word is what an operator greps the ledger for — the
   * same rule `migrationTypeLabel` follows for an unknown migration type.
   *
   * A reservation written before per-device licensing carries no `mode` column
   * at all; the store reads its absence as `campaign`, which is what keeps a
   * pre-deploy wave legible as a campaign rather than as a one-device wave.
   */
  mode: string
  reservedAt: string
  /**
   * `null`, NEVER `''`, for a migration still in flight — the server
   * normalises it precisely so a consumer cannot mistake an open wave for one
   * settled at the empty string, or render it blank beside a real timestamp.
   */
  settledAt: string | null
  reservedCount: number
  burnedCount: number
  releasedCount: number
}

export interface AccountUsageBucketTotals {
  migrations: number
  reservedCount: number
  burnedCount: number
  releasedCount: number
}

export interface AccountUsageTotals extends AccountUsageBucketTotals {
  // SPLIT BY BUCKET because the two are billed differently: a consumable burn
  // is a charge, a pool slot is capacity bought once and returned. A single
  // blended figure would invite billing a pool customer per migration.
  consumable: AccountUsageBucketTotals
  pool: AccountUsageBucketTotals
  /**
   * Reservations excluded from the window because their `reservedAt` could not
   * be parsed, and which therefore belong to no period. Expected to be 0;
   * anything else is an operator signal rather than a rounding detail, because
   * on a billing read a quietly missing migration is a quietly wrong invoice.
   */
  unanchored: number
}

export interface AccountUsageWindow {
  /** Inclusive, canonicalised by the server rather than echoed as typed. */
  from: string
  /** EXCLUSIVE. */
  to: string
  /** Always `reserved`. Echoed so a reader can never be unsure which question the report answered. */
  anchor: string
}

export interface AccountUsageResponse {
  accountId: string
  window: AccountUsageWindow
  migrations: AccountUsageMigration[]
  totals: AccountUsageTotals
}
